import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { consume } from '@lit/context';
import '../components/cos-icon.js';
import { controlErrorClass, controlErrorStyles, fieldMessage, nextFieldMessageId, visuallyHiddenStyles } from './field-anatomy.js';
import { fieldHelpDockActiveContext } from './field-help-dock-context.js';

/**
 * <cos-checkbox> — the house checkbox.
 *
 * A real `<input type="checkbox">` sits inside the label, visually
 * hidden but present and focusable — so Space-toggles, screen-reader
 * semantics and label-click association all come from the platform for
 * free, and the visible box is pure CSS drawn off `:checked`/
 * `:indeterminate`/`:focus-visible` on that sibling. Wrapping the input
 * in a `<label>` gives implicit label association with no `id`
 * plumbing needed.
 *
 * `indeterminate` has no HTML attribute — it's a JS-only property on
 * the input element — so it's set imperatively in `updated()` rather
 * than as a template binding.
 *
 * Props:
 *   - checked: boolean — controlled
 *   - label?, helpText?
 *   - error?: string (Stage: fleet/forms-primitives) — replaces helpText
 *     with error styling + message, marks the native input aria-invalid
 *   - required?: boolean (Stage: fleet/forms-primitives) — renders a `*`
 *     next to the label and sets the native input's required state
 *   - disabled?
 *   - indeterminate?: boolean — visual third state (e.g. "some of N
 *     children are checked"); does not affect `checked`
 *   - ariaLabel? (wave 3, ds-unlocks): for the box-only-in-a-table-row
 *     case where there's no room for a visible `label` at all. When set
 *     with no `label`, the native input carries it as its accessible
 *     name. If both are set, the visible `label` still wins (a visible
 *     caption is always a better accessible name than an attribute the
 *     user can't see) — `ariaLabel` is the fallback for the label-less case.
 *
 * Default slot (rich rows, ds-unlocks wave): slotted content becomes the
 * label body in place of the plain `label`/`helpText` spans — unlike
 * `cos-radio-group`'s `renderOption`, this is a real `<slot>`, so the
 * projected nodes stay in the CALLER's light DOM/shadow tree and the
 * caller's own `static styles` reach them normally; no grid-shadow trap
 * here. `label` stays the fallback (rendered when nothing is slotted —
 * see `_hasRichContent`: it is rendered BESIDE the slot, never AS the
 * slot's fallback content, because whitespace would suppress that)
 * AND the accessible name: with rich content present, the native
 * input's `aria-label` is set from `label` (or `ariaLabel`) explicitly,
 * because the implicit label-from-content name would otherwise read the
 * whole rich row — badges, meta, everything — as the checkbox's name.
 *
 * Emits `cos:change` (detail: { checked }) on toggle. Space toggles
 * (native `<input>` behaviour).
 */
@customElement('cos-checkbox')
export class CosCheckbox extends LitElement {
	@property({ type: Boolean, reflect: true }) checked = false;
	@property({ type: String }) label = '';
	@property({ type: String }) helpText = '';
	@property({ type: String }) error = '';
	@property({ type: Boolean }) required = false;
	@property({ type: Boolean, reflect: true }) disabled = false;
	@property({ type: Boolean }) indeterminate = false;
	@property({ type: String, attribute: 'aria-label' }) ariaLabel: string | null = null;

	@consume({ context: fieldHelpDockActiveContext, subscribe: true })
	@state()
	private _dockActive = false;

	@query('input') private _inputEl?: HTMLInputElement;

	/**
	 * Programmatic focus — moves to the native checkbox.
	 *
	 * See cos-select's identical override for why this exists: without it,
	 * `el.focus()` on the host silently does nothing, so FormController's
	 * focus-the-first-invalid-field never reached a checkbox. That matters
	 * most here, because a cross-field validity error (the library-rooms
	 * "a room patrons can book must also be visible to them" pair) lands on
	 * a checkbox rather than a text field.
	 */
	focus(): void {
		this._inputEl?.focus();
	}

	private readonly _msgId = nextFieldMessageId('cos-checkbox');

	// Light-DOM children ARE the slotted content — available synchronously,
	// no slotchange listener needed. Presence alone (not what's in it)
	// decides whether the label fallback renders and whether aria-label
	// needs to be forced.
	//
	// WHITESPACE IS NOT CONTENT. A whitespace-only text node is still an
	// ASSIGNED node as far as the platform is concerned, so it suppresses a
	// `<slot>`'s fallback content — which is why the label body below is
	// rendered OUTSIDE the slot and gated on this getter rather than left as
	// slot fallback. Every multi-line call site (`…}}>\n</cos-checkbox>`)
	// carries such a node, so the slot-fallback form silently dropped the
	// visible label on 10 sites across Attend/project/ticket surfaces.
	private get _hasRichContent(): boolean {
		if (this.children.length > 0) return true;
		for (const n of Array.from(this.childNodes)) {
			if (n.nodeType === Node.TEXT_NODE && (n.textContent ?? '').trim() !== '') return true;
		}
		return false;
	}

	private _ariaLabelValue(): string | typeof nothing {
		if (this._hasRichContent) {
			// Rich content replaces the plain label/helpText body; without an
			// explicit name here the browser would read the whole row
			// (badges, meta, everything) as the accessible name.
			return this.label || this.ariaLabel || nothing;
		}
		return !this.label && this.ariaLabel ? this.ariaLabel : nothing;
	}

	protected updated(changed: Map<string, unknown>): void {
		if (changed.has('indeterminate') && this._inputEl) {
			this._inputEl.indeterminate = this.indeterminate;
		}
	}

	protected firstUpdated(): void {
		if (this._inputEl) this._inputEl.indeterminate = this.indeterminate;
	}

	private _onChange(e: Event): void {
		this.checked = (e.target as HTMLInputElement).checked;
		this.indeterminate = false;
		this.dispatchEvent(
			new CustomEvent('cos:change', {
				detail: { checked: this.checked },
				bubbles: true,
				composed: true,
			}),
		);
	}

	static styles = css`
		:host {
			display: inline-block;
			font-size: var(--cos-font-size-sm);
			color: var(--cos-text-primary);
		}
		.wrap {
			display: inline-flex;
			align-items: flex-start;
			gap: var(--cos-space-2);
			cursor: pointer;
		}
		:host([disabled]) .wrap {
			cursor: not-allowed;
			opacity: 0.6;
		}
		.native {
			position: absolute;
			width: 1px;
			height: 1px;
			opacity: 0;
			margin: 0;
			pointer-events: none;
		}
		.box {
			flex: none;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			width: 18px;
			height: 18px;
			margin-top: 1px;
			box-sizing: border-box;
			border: 1.5px solid var(--cos-border);
			border-radius: var(--cos-radius-xs);
			background: var(--cos-surface);
			transition: background-color var(--cos-transition-fast), border-color var(--cos-transition-fast);
			color: var(--cos-text-on-color);
		}
		.native:checked + .box,
		.native:indeterminate + .box {
			background: var(--cos-brand-500);
			border-color: var(--cos-brand-500);
		}
		.native:focus-visible + .box {
			outline: none;
			box-shadow: var(--cos-shadow-focus);
		}
		.box cos-icon {
			display: none;
		}
		.native:checked + .box cos-icon {
			display: block;
		}
		.dash {
			display: none;
			width: 9px;
			height: 2px;
			background: currentColor;
			border-radius: 1px;
		}
		.native:indeterminate + .box .dash {
			display: block;
		}
		.native:indeterminate + .box cos-icon {
			display: none;
		}
		.text {
			display: flex;
			flex-direction: column;
			gap: 2px;
		}
		.label-text {
			line-height: 1.3;
		}
		.req {
			color: var(--cos-status-danger-text);
		}
		.help {
			font-size: var(--cos-font-size-xs);
			color: var(--cos-text-secondary);
		}
		.help.error {
			color: var(--cos-status-danger-text);
		}
		${controlErrorStyles}
		${visuallyHiddenStyles}
	`;

	render() {
		const { message, isError } = fieldMessage(this.helpText, this.error);
		const helpVisuallyHidden = this._dockActive && !isError;
		return html`
			<label class="wrap">
				<input
					class="native"
					type="checkbox"
					.checked=${this.checked}
					?required=${this.required}
					?disabled=${this.disabled}
					aria-label=${this._ariaLabelValue()}
					aria-invalid=${this.error ? 'true' : 'false'}
					aria-describedby=${message ? this._msgId : nothing}
					@change=${this._onChange}
				/>
				<span class="box${controlErrorClass(!!this.error)}" aria-hidden="true">
					<cos-icon name="check" size="12"></cos-icon>
					<span class="dash"></span>
				</span>
				<slot></slot>
				${!this._hasRichContent && (this.label || message)
					? html`<span class="text">
							${this.label
								? html`<span class="label-text"
										>${this.label}${this.required
											? html`<span class="req" aria-hidden="true"> *</span>`
											: nothing}</span
									>`
								: ''}
							${message
								? html`<span
										id=${this._msgId}
										class=${`help${isError ? ' error' : ''}${helpVisuallyHidden ? ' visually-hidden' : ''}`}
										>${message}</span
									>`
								: ''}
						</span>`
					: ''}
			</label>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'cos-checkbox': CosCheckbox;
	}
}
